// Vue core libraries - Pinia, VueUse & Vue
import { acceptHMRUpdate, defineStore } from "pinia";
import { computed, ref } from "vue";

// TMI - content type and query definitions
import { Author } from "@tmi/types/entities";

import { authors, cache, CacheClosedError } from "@/services/cache";
import { authorSeedData } from "@/services/seed-service";

// Non-reactive variables
import { createDefaultLogger } from "@/types/console-logger";
const logger = createDefaultLogger();

// State values
const initialized = computed< boolean >( () => allAuthors.value.length > 0 );
const loading = ref< boolean >( false );
const refreshing = ref< boolean >( false );

const defaultAuthors: Author[] = [];
const allAuthors = ref< Author[] >( defaultAuthors );

export const useAuthorsStore = defineStore( 'authors', () => {
    function reset(): void {
        allAuthors.value.splice( 0 );
        loading.value = false;
        refreshing.value = false;
    }

    async function initialize(): Promise< void > {
        if ( initialized.value || loading.value ) {
            logger.debug( '[ Authors ].initialize() exiting - already processing...' );
            return;
        }

        try {
            logger.debug( `[ Authors ] Checking the local database for any authors...` );

            loading.value = true;

            const t0: number = performance.now();

            // Wait for the database to open before the first read.
            await cache.ready();

            // If anything exists within the local database, load it
            if ( !await authors.isEmpty() ) {
                allAuthors.value = await authors.all();
                const t1: number = performance.now();
                logger.info( `(local) Loaded ${ allAuthors.value.length } authors in ${ t1 - t0 } ms` );
            } else {
                allAuthors.value = authorSeedData();

                const t1: number = performance.now();
                logger.info( `Fetching & mapping ${ allAuthors.value.length } author items took ${ t1 - t0 } ms` );
            }
        } catch ( e: any ) {
            if ( e instanceof CacheClosedError ) { return; }   // a newer build upgraded the cache; this tab is reloading
            logger.error( JSON.stringify( e.message ) );
            reset();
        } finally {
            loading.value = false;
            refreshing.value = false;
        }
    }

    /**
     * Retrieves an Author object by its contentful sys.id value or returns undefined if the given
     * sys.id value is not found within the in-memory collection.
     *
     * @param {string} sysId - the contentful assigned sys.id (unique identifier within the contentful space/environment
     * @returns {Author | undefined} - the Author object previously loaded
     */
    function authorById( sysId: string ) : Author | undefined {
        return allAuthors.value.find( ( author: Author ): boolean => sysId === author.id )
    }

    function authorBySlug( slug: string ): Author | undefined {
        return allAuthors.value.find( ( author: Author ): boolean => slug === author.slug );
    }

    return {
        // ACTIONS
        authorById,
        authorBySlug,
        initialize,
        reset,

        // GETTERS
        allAuthors
    }
} );

if ( import.meta.hot ) {
    import.meta.hot.accept( acceptHMRUpdate( useAuthorsStore, import.meta.hot ) );
}
